/**
 * The SEO surface of the client: the routes that exist for search traffic, and the data behind them.
 *
 * The URLs are Serbian and readable on purpose. "/polovni-automobili/audi/a4" is itself a ranking
 * signal and, more usefully, is legible in a search result and in a message — which matters on a
 * marketplace where a good share of traffic is one person sending a link to another.
 */

/** Root of the category tree. Chosen because it is the query people type. */
export const CATEGORY_SEGMENT = 'polovni-automobili';

export const BUY_GUIDE_SLUG = 'kupujem-auto';
export const SELL_GUIDE_SLUG = 'prodajem-auto';

export const SITE_NAME = 'KojaKola';

export function categoryPath(makeSlug?: string, modelSlug?: string): string {
  if (!makeSlug) {
    return `/${CATEGORY_SEGMENT}`;
  }

  return modelSlug
    ? `/${CATEGORY_SEGMENT}/${makeSlug}/${modelSlug}`
    : `/${CATEGORY_SEGMENT}/${makeSlug}`;
}

export interface SeoFaqEntry {
  question: string;
  answer: string;
}

export interface SeoGuideContent {
  slug: string;
  heading: string;
  intro: string;
  faq: SeoFaqEntry[];
}

export interface SeoContent {
  homeFaq: SeoFaqEntry[];
  guides: SeoGuideContent[];
}

export interface SeoMake {
  id: number;
  name: string;
  slug: string;
  listingCount: number;
}

export interface SeoModel extends SeoMake {
  makeId: number;
  makeName: string;
  makeSlug: string;
}

export interface SeoCatalogue {
  totalListings: number;
  makes: SeoMake[];
}

export interface SeoMakeCatalogue {
  make: SeoMake;
  models: SeoModel[];
}

/**
 * Serbian has three plural forms and the teens break the obvious rule.
 *
 * Duplicated from the server's `SeoText.Plural` rather than fetched, because a count rendered on the
 * page has to agree with the count in the title the server already wrote — and a network round trip
 * to decide between "oglas" and "oglasa" would be absurd.
 */
export function plural(count: number, one: string, few: string, many: string): string {
  const mod10 = count % 10;
  const mod100 = count % 100;

  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;

  return many;
}
